/**
 * KANDI BROWSER
 * 
 * Features:
 * - Sort by Bestselling/Newest/A-Z/Price
 * - Floating cart indicator
 * - Smart empty state with popular suggestions
 * - Background loading for instant filtering
 */

(function() {
  'use strict';

  const CONFIG = {
    perPage: 48,
    filters: ['all', 'party-hard', 'edm-life', 'good-vibes', 'spoon']
  };

  // DOM Elements
  const elements = {
    browser: document.getElementById('kandi-browser'),
    grid: document.getElementById('kandi-grid'),
    search: document.getElementById('kandi-search'),
    searchClear: document.getElementById('search-clear'),
    searchTerm: document.getElementById('search-term'),
    sort: document.getElementById('kandi-sort'),
    tabs: document.querySelectorAll('.tab-btn'),
    visibleCount: document.getElementById('visible-count'),
    noResults: document.getElementById('no-results'),
    suggestionsGrid: document.getElementById('suggestions-grid'),
    resetFilters: document.getElementById('reset-filters'),
    floatingCart: document.getElementById('floating-cart'),
    floatingCartCount: document.getElementById('floating-cart-count'),
    cards: null
  };

  // State
  const state = {
    collectionHandle: '',
    collectionUrl: '',
    currentPage: 1,
    totalPages: 1,
    isLoading: false,
    allLoaded: false,
    activeFilter: 'all',
    searchQuery: '',
    sortBy: 'bestselling',
    debounceTimer: null,
    cart: {},
    cartCount: 0,
    cartUpdating: false,
    tagCounts: {},
    popularProducts: [],
    originalOrder: []
  };

  // ============================================
  // Initialize
  // ============================================
  
  function init() {
    if (!elements.browser || !elements.grid) return;

    state.collectionHandle = elements.browser.dataset.collectionHandle || '';
    state.collectionUrl = elements.browser.dataset.collectionUrl || '';
    state.currentPage = parseInt(elements.grid.dataset.currentPage) || 1;
    state.totalPages = parseInt(elements.grid.dataset.totalPages) || 1;
    
    elements.cards = elements.grid.querySelectorAll('.kandi-card');
    
    // Store original order (this IS bestselling order from Shopify)
    storeOriginalOrder();
    
    // Identify popular products for suggestions
    identifyPopularProducts();
    
    bindEvents();
    initCartControls();
    fetchCart();
    fetchTagCounts();
    
    updateLocalCounts();
    applyFilters();
    
    if (state.currentPage < state.totalPages) {
      loadAllProductsInBackground();
    } else {
      state.allLoaded = true;
    }
  }
  
  // ============================================
  // Original Order Tracking
  // ============================================
  
  function storeOriginalOrder() {
    elements.cards = elements.grid.querySelectorAll('.kandi-card');
    state.originalOrder = [];
    elements.cards.forEach((card, index) => {
      card.dataset.originalIndex = index;
      state.originalOrder.push(card.dataset.productId);
    });
  }

  // ============================================
  // Popular Products for Suggestions
  // ============================================
  
  function identifyPopularProducts() {
    elements.cards = elements.grid.querySelectorAll('.kandi-card');
    state.popularProducts = [];
    
    // First items in original order are bestsellers (Shopify's sort)
    // Use those for suggestions
    const cardsArray = Array.from(elements.cards);
    cardsArray.sort((a, b) => {
      return (parseInt(a.dataset.originalIndex) || 0) - (parseInt(b.dataset.originalIndex) || 0);
    });
    
    // Take top 6 for suggestions
    cardsArray.slice(0, 6).forEach(card => {
      state.popularProducts.push({
        element: card.cloneNode(true),
        title: card.dataset.title
      });
    });
  }

  // ============================================
  // Sorting
  // ============================================
  
  function sortProducts() {
    elements.cards = elements.grid.querySelectorAll('.kandi-card');
    const cardsArray = Array.from(elements.cards);
    
    cardsArray.sort((a, b) => {
      switch (state.sortBy) {
        case 'bestselling':
          // Restore original Shopify order (which is bestselling)
          const aIndex = parseInt(a.dataset.originalIndex) || 0;
          const bIndex = parseInt(b.dataset.originalIndex) || 0;
          return aIndex - bIndex;
          
        case 'newest':
          const aCreated = parseInt(a.dataset.created) || 0;
          const bCreated = parseInt(b.dataset.created) || 0;
          return bCreated - aCreated;
          
        case 'alpha-asc':
          return (a.dataset.title || '').localeCompare(b.dataset.title || '');
          
        case 'alpha-desc':
          return (b.dataset.title || '').localeCompare(a.dataset.title || '');
          
        case 'price-asc':
          const aPrice = parseInt(a.dataset.price) || 0;
          const bPrice = parseInt(b.dataset.price) || 0;
          return aPrice - bPrice;
          
        case 'price-desc':
          const aPriceD = parseInt(a.dataset.price) || 0;
          const bPriceD = parseInt(b.dataset.price) || 0;
          return bPriceD - aPriceD;
          
        default:
          return 0;
      }
    });
    
    // Re-append in sorted order
    cardsArray.forEach(card => elements.grid.appendChild(card));
    elements.cards = elements.grid.querySelectorAll('.kandi-card');
  }

  // ============================================
  // Fetch True Tag Counts from Backend
  // ============================================
  
  async function fetchTagCounts() {
    const baseUrl = state.collectionUrl || `/collections/${state.collectionHandle}`;
    
    const fetchPromises = CONFIG.filters.map(async (tag) => {
      const url = tag === 'all' 
        ? `${baseUrl}?section_id=kandi-collection-browser`
        : `${baseUrl}/${tag}?section_id=kandi-collection-browser`;
      
      try {
        const response = await fetch(url);
        if (response.ok) {
          const html = await response.text();
          const count = extractCountFromHtml(html, tag);
          updateCountDisplay(tag, count);
        }
      } catch (e) {}
    });
    
    await Promise.all(fetchPromises);
  }
  
  function extractCountFromHtml(html, tag) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Count visible cards on page 1
    const cards = doc.querySelectorAll('.kandi-card');
    const grid = doc.getElementById('kandi-grid');
    
    if (!grid) return cards.length;
    
    const totalPages = parseInt(grid.dataset.totalPages) || 1;
    const perPage = parseInt(grid.dataset.perPage) || 48;
    
    if (totalPages === 1) {
      // Single page - card count is accurate
      return cards.length;
    }
    
    // Multiple pages - use Shopify's count (may include sold-out, but close enough)
    // This is a tradeoff: exact count would require fetching all pages
    const shopifyCount = parseInt(grid.dataset.totalProducts) || 0;
    return shopifyCount;
  }
  
  function updateCountDisplay(tag, count) {
    state.tagCounts[tag] = count;
    const countEl = document.querySelector(`[data-count="${tag}"]`);
    if (countEl) countEl.textContent = count;
  }

  // ============================================
  // Background Loading (Silent)
  // ============================================
  
  async function loadAllProductsInBackground() {
    while (state.currentPage < state.totalPages && !state.isLoading) {
      await loadNextPage();
      await new Promise(r => setTimeout(r, 50));
    }
    state.allLoaded = true;
    identifyPopularProducts();
    updateLocalCounts();
    sortProducts();
    applyFilters();
  }
  
  async function loadNextPage() {
    if (state.isLoading) return;
    
    state.isLoading = true;
    const nextPage = state.currentPage + 1;
    
    try {
      const url = `${state.collectionUrl}?page=${nextPage}&section_id=kandi-collection-browser`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network error');
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newCards = doc.querySelectorAll('.kandi-card');
      
      if (newCards.length > 0) {
        const currentCount = elements.grid.querySelectorAll('.kandi-card').length;
        
        newCards.forEach((card, i) => {
          const clone = card.cloneNode(true);
          // Assign original index to maintain bestselling order
          clone.dataset.originalIndex = currentCount + i;
          elements.grid.appendChild(clone);
          bindCardCartControls(clone);
        });
        
        state.currentPage = nextPage;
        elements.cards = elements.grid.querySelectorAll('.kandi-card');
        
        // Re-sort if not on bestselling (to insert new cards correctly)
        if (state.sortBy !== 'bestselling') {
          sortProducts();
        }
        
        updateCartDisplay();
      }
    } catch (error) {}
    finally {
      state.isLoading = false;
    }
  }

  // ============================================
  // Filtering & Events
  // ============================================
  
  function bindEvents() {
    // Sort dropdown
    if (elements.sort) {
      elements.sort.addEventListener('change', (e) => {
        state.sortBy = e.target.value;
        sortProducts();
        applyFilters();
      });
    }
    
    // Tab clicks
    elements.tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        setActiveFilter(tab.dataset.filter);
      });
    });
    
    // Search input
    if (elements.search) {
      elements.search.addEventListener('input', (e) => {
        clearTimeout(state.debounceTimer);
        state.debounceTimer = setTimeout(() => {
          state.searchQuery = e.target.value.toLowerCase().trim();
          applyFilters();
        }, 150);
      });
    }
    
    // Search clear
    if (elements.searchClear) {
      elements.searchClear.addEventListener('click', () => {
        elements.search.value = '';
        state.searchQuery = '';
        applyFilters();
      });
    }
    
    // Reset filters
    if (elements.resetFilters) {
      elements.resetFilters.addEventListener('click', () => {
        elements.search.value = '';
        state.searchQuery = '';
        setActiveFilter('all');
      });
    }
  }
  
  function setActiveFilter(filter) {
    state.activeFilter = filter;
    
    elements.tabs.forEach(tab => {
      const isActive = tab.dataset.filter === filter;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive);
    });
    
    applyFilters();
  }
  
  function applyFilters() {
    elements.cards = elements.grid.querySelectorAll('.kandi-card');
    let visibleCount = 0;
    
    elements.cards.forEach(card => {
      const tags = card.dataset.tags || '';
      const title = card.dataset.title || '';
      
      const filterMatch = state.activeFilter === 'all' || tags.includes(state.activeFilter);
      const searchMatch = !state.searchQuery || title.includes(state.searchQuery);
      
      const visible = filterMatch && searchMatch;
      card.style.display = visible ? '' : 'none';
      
      if (visible) visibleCount++;
    });
    
    if (elements.visibleCount) {
      elements.visibleCount.textContent = visibleCount;
    }
    
    if (elements.searchClear) {
      elements.searchClear.style.opacity = state.searchQuery ? '1' : '0';
      elements.searchClear.style.pointerEvents = state.searchQuery ? 'auto' : 'none';
    }
    
    // Handle no results
    if (elements.noResults) {
      const showNoResults = visibleCount === 0;
      elements.noResults.hidden = !showNoResults;
      
      if (showNoResults) {
        const noResultsTitle = elements.noResults.querySelector('h3');
        const noResultsText = elements.noResults.querySelector('p');
        
        if (state.searchQuery) {
          // No search results
          if (elements.searchTerm) {
            elements.searchTerm.textContent = state.searchQuery;
          }
          if (noResultsTitle) noResultsTitle.innerHTML = 'No matches for "<span id="search-term">' + state.searchQuery + '</span>"';
          if (noResultsText) noResultsText.textContent = "We couldn't find any bracelets matching your search.";
        } else if (state.activeFilter !== 'all') {
          // No filter results (unlikely but possible)
          if (noResultsTitle) noResultsTitle.textContent = 'No bracelets in this category';
          if (noResultsText) noResultsText.textContent = 'Try selecting a different filter.';
        } else {
          // No products at all (very unlikely)
          if (noResultsTitle) noResultsTitle.textContent = 'No bracelets available';
          if (noResultsText) noResultsText.textContent = 'Check back soon!';
        }
        populateSuggestions();
      }
    }
  }
  
  function populateSuggestions() {
    if (!elements.suggestionsGrid || state.popularProducts.length === 0) return;
    
    elements.suggestionsGrid.innerHTML = '';
    
    state.popularProducts.slice(0, 4).forEach(item => {
      const card = item.element.cloneNode(true);
      card.style.display = '';
      
      bindCardCartControls(card);
      elements.suggestionsGrid.appendChild(card);
    });
  }
  
  function updateLocalCounts() {
    elements.cards = elements.grid.querySelectorAll('.kandi-card');
    
    const counts = { 'all': 0, 'party-hard': 0, 'edm-life': 0, 'good-vibes': 0, 'spoon': 0 };
    
    elements.cards.forEach(card => {
      const tags = card.dataset.tags || '';
      counts['all']++;
      CONFIG.filters.forEach(f => {
        if (f !== 'all' && tags.includes(f)) counts[f]++;
      });
    });
    
    CONFIG.filters.forEach(filter => {
      if (state.tagCounts[filter] === undefined) {
        const countEl = document.querySelector(`[data-count="${filter}"]`);
        if (countEl && countEl.textContent === '--') {
          countEl.textContent = counts[filter];
        }
      }
    });
  }

  // ============================================
  // Cart Management
  // ============================================
  
  async function fetchCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      state.cart = {};
      state.cartCount = cart.item_count;
      cart.items.forEach(item => {
        // Ensure variant_id is stored as string to match dataset attributes
        state.cart[String(item.variant_id)] = item.quantity;
      });
      
      updateCartDisplay();
      updateFloatingCart();
    } catch (error) {}
  }
  
  function updateCartDisplay() {
    elements.cards = elements.grid.querySelectorAll('.kandi-card');
    
    elements.cards.forEach(card => {
      const variantId = card.dataset.variantId;
      const quantity = state.cart[variantId] || 0;
      
      const controls = card.querySelector('.cart-controls');
      if (!controls) return;
      
      const addBtn = controls.querySelector('.quick-add-btn');
      const qtyControls = controls.querySelector('.qty-controls');
      const qtyCount = controls.querySelector('.qty-count');
      
      if (quantity > 0) {
        if (addBtn) addBtn.hidden = true;
        if (qtyControls) qtyControls.hidden = false;
        if (qtyCount) qtyCount.textContent = quantity;
      } else {
        if (addBtn) addBtn.hidden = false;
        if (qtyControls) qtyControls.hidden = true;
      }
    });
  }
  
  function updateFloatingCart() {
    if (!elements.floatingCart) return;
    
    if (state.cartCount > 0) {
      elements.floatingCart.hidden = false;
      if (elements.floatingCartCount) {
        elements.floatingCartCount.textContent = state.cartCount;
      }
    } else {
      elements.floatingCart.hidden = true;
    }
  }
  
  function initCartControls() {
    elements.cards = elements.grid.querySelectorAll('.kandi-card');
    elements.cards.forEach(card => bindCardCartControls(card));
  }
  
  function bindCardCartControls(card) {
    const controls = card.querySelector('.cart-controls');
    if (!controls) return;
    
    const variantId = controls.dataset.variantId;
    const addBtn = controls.querySelector('.quick-add-btn');
    const minusBtn = controls.querySelector('.qty-minus');
    const plusBtn = controls.querySelector('.qty-plus');
    
    if (addBtn) {
      addBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        updateCartQuantity(variantId, 1);
      });
    }
    
    if (minusBtn) {
      minusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const current = state.cart[variantId] || 0;
        updateCartQuantity(variantId, current - 1);
      });
    }
    
    if (plusBtn) {
      plusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const current = state.cart[variantId] || 0;
        updateCartQuantity(variantId, current + 1);
      });
    }
  }
  
  async function updateCartQuantity(variantId, newQuantity) {
    if (state.cartUpdating) return;
    state.cartUpdating = true;
    
    const oldQuantity = state.cart[variantId] || 0;
    const quantityDiff = newQuantity - oldQuantity;
    
    // Optimistic update
    if (newQuantity > 0) {
      state.cart[variantId] = newQuantity;
    } else {
      delete state.cart[variantId];
    }
    state.cartCount = Math.max(0, state.cartCount + quantityDiff);
    updateCartDisplay();
    updateFloatingCart();
    
    try {
      if (newQuantity <= 0) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 0 })
        });
      } else if (oldQuantity === 0) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: newQuantity }] })
        });
      } else {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: newQuantity })
        });
      }
      
      updateHeaderCartCount();
      
    } catch (error) {
      // Revert on error
      if (oldQuantity > 0) {
        state.cart[variantId] = oldQuantity;
      } else {
        delete state.cart[variantId];
      }
      state.cartCount = Math.max(0, state.cartCount - quantityDiff);
      updateCartDisplay();
      updateFloatingCart();
    } finally {
      state.cartUpdating = false;
    }
  }
  
  async function updateHeaderCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      state.cartCount = cart.item_count;
      
      const selectors = [
        '.cart-count',
        '.cart-count-bubble',
        '[data-cart-count]',
        '.js-cart-count',
        '#cart-icon-bubble span',
        '.header__cart-count'
      ];
      
      selectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          el.textContent = cart.item_count;
        });
      });
      
      updateFloatingCart();
    } catch (e) {}
  }

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
